// import React from 'react';
import { styled } from '@mui/material/styles';
import { Affinity, DualUseGoodsClobDto } from 'models/dtoTypes';
import SubSection from './_subSection';
import SdgScoreClob, { ISdgData } from './_sdgScoreClob';
import Empty from './_empty';
import { dualUseGoods } from 'kosmos/images';
import EsgLegand from './_esgLegand';
import { smartGovernancePrimaryColor } from 'kosmosConfig';
import { useTranslation } from 'react-i18next';

const PREFIX = '_dualUseGoodsReport';

const classes = {
  report: `${PREFIX}-report`,
};

const Root = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  [`& .${classes.report}`]: {},
}));

interface IProps {
  name?: string;
  dualUseGoodsReport: DualUseGoodsClobDto[];
  dualUseGoodsReportSmartGovernance?: DualUseGoodsClobDto[];
}

export default function DualUseGoodsReport(props: IProps): JSX.Element {
  const { t } = useTranslation('default');
  const { dualUseGoodsReport, dualUseGoodsReportSmartGovernance } = props;

  const renderSingleClob = (item: DualUseGoodsClobDto, index: number) => {
    const { entitySummaryCode, entitySummaryDetail, affinity } = item;
    const secondarySgItem = dualUseGoodsReportSmartGovernance?.length
      ? dualUseGoodsReportSmartGovernance[index]
      : undefined;
    const secondaryAffinity = secondarySgItem?.affinity ?? Affinity.Unscored;
    const code: string = entitySummaryCode || t('unknown');
    const desc: string = entitySummaryDetail || t('unknown');
    const title = `${code} - ${desc}`;

    return (
      <SdgScoreClob
        withBorder={true}
        withBg={true}
        key={index}
        title={title}
        affinity={affinity}
        secondaryAffinity={secondaryAffinity}
      />
    );
  };

  const renderLegands = () => {
    const legands: ISdgData[] = [
      {
        affinity: 'negative',
        title: t('business-products-or-services-identified-as-used-for-military-purpose'),
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'warning',
        title: t(
          'business-products-or-services-identified-as-a-dualuse-good-and-potentially-used-for-military-purpose',
        ),
        withBg: true,
        size: 'small',
      },
      {
        affinity: 'positive',
        title: t('business-products-or-services-identified-as-nondualuse-good'),
        withBg: true,
        size: 'small',
      },
    ];

    return (
      <EsgLegand
        legands={legands}
        title={t('activity-check-against-dualuse-goods-and-products-lists')}
        learnMoreTabId="dug"
      />
    );
  };

  const renderHeader = () => {
    const style = {
      display: 'inline-block',
      width: 50,
      marginRight: 10,
    };
    return (
      <div>
        <div style={{ ...style, textAlign: 'center' }}>{t('auto')}</div>
        <div
          style={{
            ...style,
            color: smartGovernancePrimaryColor,
            textAlign: 'center',
          }}
        >
          {t('sg')}
        </div>
      </div>
    );
  };
  const renderContent = () => {
    if (dualUseGoodsReport.length === 0) {
      return <Empty title={t('there-is-no-data-available')} />;
    }

    return (
      <Root>
        {renderHeader()}
        {dualUseGoodsReport.map(renderSingleClob)}
      </Root>
    );
  };

  return (
    <SubSection title={t('goods-and-service-check')} isBold icon={<img src={dualUseGoods} />}>
      <>
        {renderLegands()}
        {renderContent()}
      </>
    </SubSection>
  );
}
